import { Component, OnInit, signal, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { PropertyChargeScheduleModel } from '../../../../core/models/data/property-charge-schedule.model';
import { ChargesService } from '../../../../core/services/charge/charge.service';
import { ChargeScheduleForm } from '../../../../forms/charge-schedule/charge-schedule.form';
import { FormState } from '../../../../core/enums/forms.enums';
import { PropertyStatuses } from '../../../../core/enums/data.enums';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
  selector: 'ms-property-charge-schedule-edit-page',
  imports: [PageTitleComponent, CommonModule, ChargeScheduleForm],
  templateUrl: './property-charge-schedule-edit.page.html',
})
export class PropertyChargeScheduleEditPage implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private chargesService = inject(ChargesService);

  public propertyChargeScheduleId: number | undefined;
  public schedule = signal<PropertyChargeScheduleModel | undefined>(undefined);
  public openInvoicesCount = signal<number>(0);
  public isLoading = signal<boolean>(true);

  public FormState = FormState;

  public ngOnInit(): void {
    this.propertyChargeScheduleId = +(this.activatedRoute.snapshot.paramMap.get('propertyChargeScheduleId') ?? NaN) || undefined;

    if (!this.propertyChargeScheduleId) {
      this.router.navigate(['system', '404']);
      return;
    }

    this.loadSchedule();
  }

  private async loadSchedule(): Promise<void> {
    this.isLoading.set(true);

    try {
      const [scheduleResponse, summaryResponse] = await Promise.all([
        this.chargesService.getPropertyChargeScheduleById(this.propertyChargeScheduleId!, true),
        this.chargesService.getPropertyChargeScheduleSummaryById(this.propertyChargeScheduleId!)
      ]);

      if (!scheduleResponse.success || !scheduleResponse.data) {
        this.router.navigate(['system', '404']);
        return;
      }

      // Deleted schedules are read-only — editing is not allowed even via a direct link.
      if (scheduleResponse.data.status === PropertyStatuses.Deleted) {
        this.router.navigate(['/invoice-schedules/summary', this.propertyChargeScheduleId]);
        return;
      }

      this.schedule.set(scheduleResponse.data);
      if (summaryResponse.success && summaryResponse.data) {
        this.openInvoicesCount.set(summaryResponse.data.openChargesCount ?? 0);
      }
    } catch (error) {
      console.error('Error loading property charge schedule:', error);
      this.router.navigate(['system', '404']);
    } finally {
      this.isLoading.set(false);
    }
  }
}
